import React, { ChangeEvent, FC, memo, useCallback, useEffect, useState } from 'react';
import testIds from '@smart-tv/e2e/shared/variables';

import Button from '@smart-tv/components/button/button';
import Input from '@smart-tv/components/input';
import Typography from '@smart-tv/components/typography';

import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';

import { FavoritesScreen } from '@smart-tv/contexts/favorites-edit-context/favorites-edit-context';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import styles from './favorites.module.scss';

const MAX_NAME_LENGTH = 14;

export const FavoritesTitle: FC = () => {
  const { ref, focusKey, focusSelf } = useFocusable({ isFocusBoundary: true });

  const {
    editedFavoritesList,
    usedFavoritesLists,
    handleSelectScreen,
    handleEditedFavoritesListNameChange
  } = useFavoritesEdit();

  const [titleValue, setTitleValue] = useState<string>(
    editedFavoritesList ? editedFavoritesList.name : ''
  );
  const [error, setError] = useState(false);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setError(false);
    setTitleValue(event.target.value);
  }, []);

  const handleTitleChange = useCallback(() => {
    if (usedFavoritesLists?.some((favoriteslist) => favoriteslist.name === titleValue)) {
      return setError(true);
    }

    if (titleValue.trim().length <= MAX_NAME_LENGTH) {
      handleEditedFavoritesListNameChange(titleValue);
      handleSelectScreen(FavoritesScreen.Edit);
    }
  }, [handleEditedFavoritesListNameChange, handleSelectScreen, titleValue, usedFavoritesLists]);

  useEffect(() => {
    focusSelf();
  }, [focusSelf]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className={styles['favorites-title-layout']}
        ref={ref}
        data-testid={testIds.favorites.rename.root}
      >
        <Typography
          className={styles['favorites-title-root--header']}
          variant="h1"
          data-testid={testIds.favorites.rename.title}
        >
          Navngiv kanalliste
        </Typography>
        <Input
          className={styles['favorites-title-root--input']}
          type="text"
          maxLength={MAX_NAME_LENGTH}
          value={titleValue}
          onChange={handleInputChange}
          forceKeyboard={true}
          inputProps={{ 'data-testid': testIds.favorites.rename.input }}
        />
        {error && (
          <Typography
            className={styles['favorites-title-root--error']}
            data-testid={testIds.favorites.rename.error}
          >
            Du har allerede en kanalliste med det samme navn. Skriv et andet navn.
          </Typography>
        )}
        <Button
          className={styles['favorites-title-root--button']}
          onClick={() => handleTitleChange()}
          disabled={titleValue === ''}
          data-testid={testIds.favorites.rename.save}
        >
          Gem
        </Button>
        <Button
          className={styles['favorites-title-root--button']}
          onClick={() => handleSelectScreen(FavoritesScreen.Edit)}
          data-testid={testIds.favorites.rename.cancel}
        >
          Fortryd
        </Button>
      </div>
    </FocusContext.Provider>
  );
};

export default memo(FavoritesTitle);
